// Draw-progress hook. Returns 0 → 1 with ease-out cubic.
// Used to drive SVG stroke-dasharray for line-draw animations
// (TrajectoryChart) and arc sweep for the ChannelMix donut.
//
// Returns 1 immediately when prefers-reduced-motion: reduce matches,
// or when the document/tab is hidden — in both cases the chart
// should render at full draw rather than empty.
//
// Cold-render defensive default: initial state is 1 (fully drawn),
// then the effect resets to 0 INSIDE the first RAF callback and
// animates back to 1. If RAF never fires (frozen iframe, hidden
// background tab, throttled accessibility user), the chart paints
// at full draw on first paint and stays there. Wrong animation,
// right content.

'use client';

import { useEffect, useState } from 'react';

export function useDrawProgress(durationMs = 1200, delay = 0): number {
  const [p, setP] = useState(1);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setP(1);
      return;
    }

    setP(0);
    let raf = 0;
    let start: number | undefined;
    const tick = (t: number) => {
      if (start === undefined) start = t;
      const elapsed = t - start - delay;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const np = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - np, 3);
      setP(eased);
      if (np < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, delay]);

  return p;
}
